import { getOrderById } from '@util/firestore/orders';
import { useQuery } from '@tanstack/react-query';
import { useRef } from 'react';
import { useHelpWithOrder } from '../hooks/useHelpWithOrder';
import FormLabel from '../../../components/forms/controls/FormLabel';
import FormSelect from '../../../components/forms/controls/FormSelect';
import Button from '@ui/Button';
import { ChevronRightIcon, PackageIcon, XIcon } from 'lucide-react';
import TrackOrderModal from '@c/modals/TrackOrderModal';
import SafeImage from '@ui/SafeImage';
import FormTextarea from '../../../components/forms/controls/FormTextarea';
import { LogoWithoutText } from '@c/icons/logoWithoutText';
import dayjs from 'dayjs';
import { formatCurrency } from '@util/index';

interface HelpRequestFormProps {
  order_id: string;
  dismiss?: () => void;
  variant?: 'buyer' | 'seller';
}

export default function HelpRequestForm({
  order_id,
  dismiss,
  variant,
}: HelpRequestFormProps) {
  const { data: order } = useQuery({
    queryKey: ['order', order_id],
    queryFn: () => getOrderById(order_id!),
    enabled: !!order_id,
  });

  const {
    trackModalOpen,
    setTrackModalOpen,
    handleSubmit,
    errors,
    setValue,
    control,
    register,
    onSubmit,
    selectedItemHasTracking,
    loadingSubmit,
    getOptions,
    getSubOptions,
    form,
    seller,
  } = useHelpWithOrder({ order, dismiss, variant });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const imageIssue = ["It's different than expected"].includes(form.issue);
  const subOptions = getSubOptions(form.issue);

  const getRefundAmount = () => {
    if (!order) return formatCurrency(0);
    if (form.item_id) {
      const item = Object.values(order.sellers)
        .flat()
        .find((item) => item.id === form.item_id);

      if (!item) return formatCurrency(0);

      const subtotal =
        item.product_cost * item.qty +
        item.buyer_shipping_cost +
        item.product_tax;
      return formatCurrency(subtotal * 0.965);
    } else {
      return formatCurrency(order.total * 0.965);
    }
  };

  return (
    <form className="mt-4 flex w-full flex-col gap-[2.4rem] p-8 sm:w-[50rem] sm:max-w-[90vw]">
      <FormLabel
        value="What happened?"
        required
        className="!text-[1.8rem]"
        errorMessage={errors.issue?.message}
      >
        <FormSelect
          control={control}
          name="issue"
          allowEmpty={false}
          placeholder="Select issue"
          options={getOptions()}
          className="!text-[1.8rem] !font-medium"
        />
      </FormLabel>

      {subOptions.length > 0 && (
        <FormLabel
          value={
            form.issue === 'Request Cancellation'
              ? 'Why are you cancelling?'
              : 'Tell us more'
          }
          className="!text-[1.8rem]"
          errorMessage={errors.subissue?.message}
        >
          <FormSelect
            control={control}
            name="subissue"
            allowEmpty={false}
            placeholder="Select issue"
            options={getSubOptions(form.issue)}
            className="!text-[1.8rem] !font-medium"
          />
        </FormLabel>
      )}

      {order && variant === 'buyer' && (
        <>
          <FormLabel
            value="Which item?"
            required
            errorMessage={errors.item_id?.message}
            className="!text-[1.8rem]"
          >
            <FormSelect
              allowEmpty={false}
              options={Object.values(order.sellers)
                .flat()
                .map((item) => {
                  return {
                    label: item.product_name!,
                    value: item.id,
                    id: item.id,
                  };
                })}
              control={control}
              name={'item_id'}
              className="!text-[1.8rem] !font-medium"
            ></FormSelect>
          </FormLabel>
        </>
      )}

      {imageIssue && (
        // upload image (s)
        <div className="flex flex-col gap-[1.2rem]">
          <span className="text-brand-gray">Upload image(s) of the item</span>

          <input
            type="file"
            accept="image/*"
            multiple
            id="file-upload"
            ref={fileInputRef}
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              const images = files.map((file) => URL.createObjectURL(file));
              setValue('images', [
                ...(form.images ?? []),
                ...images.map((image) => image),
              ]);
            }}
          />
        </div>
      )}

      {selectedItemHasTracking &&
        order &&
        form.issue === "Where's my package?" && (
          <>
            <Button
              text="Track Package"
              onClick={() => setTrackModalOpen(true)}
              type="secondary"
              buttonType="button"
              className="text-[1.8rem]"
              leadingIcon={<PackageIcon className="mr-2 h-[2rem] w-[2rem]" />}
            />

            <TrackOrderModal
              orderItem={selectedItemHasTracking}
              isOpen={trackModalOpen}
              order={order}
              sellerId={selectedItemHasTracking.seller_id}
              dismiss={() => setTrackModalOpen(false)}
            />
          </>
        )}

      {!!form.images?.length && imageIssue && (
        <div className="flex gap-8" key="static">
          {form.images.map((file, index) => (
            <div className="relative" key={index}>
              <button
                className="z-2 absolute -right-4 -top-4 flex h-8 w-8 items-center justify-center rounded-full bg-brand-secondary p-1 align-top font-semibold text-white"
                onClick={() => {
                  setValue(
                    'images',
                    form.images?.filter((image) => image !== file)
                  );

                  const remainingImages =
                    form.images?.filter((image) => image !== file) ?? [];
                  if (remainingImages.length === 0 && fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }}
              >
                <XIcon className="h-8 w-8" />
              </button>

              <SafeImage
                width={50}
                height={50}
                src={file}
                alt="file"
                className="h-[5rem] w-[5rem] rounded-2xl border border-zinc-300 object-cover shadow-sm"
              />
            </div>
          ))}
        </div>
      )}

      {form.issue !== 'Request Cancellation' ? (
        <FormLabel
          value={
            form.issue === `Where's my package?`
              ? 'Message to seller (optional)'
              : form.issue === 'Something Else' ||
                form.issue === "Something's wrong with my order"
              ? 'Additional Comment'
              : 'Additional Comment (optional)'
          }
          required={
            form.issue === 'Something Else' ||
            form.issue === "Something's wrong with my order"
          }
          errorMessage={errors.comment?.message}
          className="!text-[1.8rem]"
        >
          <FormTextarea
            error={!!errors.comment}
            {...register('comment')}
            placeholder="Tell us more about your issue..."
            className="!text-[1.8rem]"
          />
        </FormLabel>
      ) : (
        <div className="space-y-4">
          <p>
            <strong>Cancellation Fee: </strong>3.5%*
          </p>
          <p>
            <strong>Refund Amount: </strong>
            {getRefundAmount()}
          </p>
          <p>
            * To help cover processing costs and protect sellers from lost
            sales, a 3.5% fee will be deducted from your refund if the seller
            approves your cancellation.
          </p>
        </div>
      )}

      {variant !== 'seller' && form.issue !== 'Request Cancellation' && (
        <span className="w-full text-[1.7rem]">
          If you need help, your seller can be contacted directly - but if you
          ever need us, we&apos;re here to help.
        </span>
      )}

      <div
        className={`mt-4 flex flex-col gap-y-12 ${
          loadingSubmit ? 'opacity-50' : ''
        }`}
      >
        {variant !== 'seller' &&
          form.issue !== 'Request Cancellation' &&
          seller && (
            <button
              type="button"
              className="flex w-full items-center justify-between pr-4"
              onClick={() => {
                handleSubmit((data) => onSubmit(data, 'seller'))();
              }}
              disabled={loadingSubmit}
            >
              <div className="flex items-center gap-x-8">
                <SafeImage
                  src={seller?.photo ?? ''}
                  alt="moto helmet"
                  width={100}
                  height={100}
                  className="h-[5rem] w-[5rem] rounded-full"
                />

                <span className="text-[1.8rem] font-semibold">
                  Message {seller.username || 'Seller'}
                </span>
              </div>
              <ChevronRightIcon className="h-[3rem] w-[3rem] text-brand-secondary" />
            </button>
          )}
        {(form.issue !== `Where's my package?` ||
          dayjs(order?.updated || order?.created).isBefore(
            dayjs().subtract(7, 'day')
          )) && (
          <button
            type="button"
            className="flex w-full items-center justify-between pr-4"
            onClick={() => {
              handleSubmit((data) => onSubmit(data, 'support'))();
            }}
            disabled={loadingSubmit}
          >
            <div className="flex items-center gap-x-8">
              <LogoWithoutText className="h-[5rem] w-[5rem]" />

              <span className="text-[1.8rem] font-semibold text-black">
                I need help from MX Locker
              </span>
            </div>
            <ChevronRightIcon className="h-[3rem] w-[3rem] text-brand-secondary" />
          </button>
        )}
      </div>
    </form>
  );
}
